import { supabase } from './supabaseClient'

/**
 * Service untuk mengelola data usage/penggunaan aplikasi
 * Menggabungkan data dari credit_usage_log dan agent_history
 */
export class UsageService {
  /**
   * Mendapatkan riwayat penggunaan dengan pagination
   * @param {string} userId - ID user
   * @param {number} page - Halaman (mulai dari 1)
   * @param {number} limit - Jumlah item per halaman (default: 200)
   * @returns {Promise<Object>} Data usage dengan pagination info
   */
  static async getUsageHistory(userId, page = 1, limit = 200) {
    try {
      const offset = (page - 1) * limit
      
      // Query untuk mendapatkan data gabungan dari credit_usage_log dan agent_history
      const { data, error, count } = await supabase
        .from('credit_usage_log')
        .select(`
          id,
          action_type,
          credits_used,
          credits_remaining,
          metadata,
          created_at
        `, { count: 'exact' })
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)
      
      if (error) throw error
      
      // Enrich data dengan informasi dari agent_history jika ada
      const enrichedData = await Promise.all(
        (data || []).map(async (usage) => {
          try {
            // Coba cari data terkait di agent_history berdasarkan metadata atau waktu
            let agentHistoryData = null
            
            if (usage.metadata?.agent_history_id) {
              const { data: historyData } = await supabase
                .from('agent_history')
                .select('title, query, agent_type')
                .eq('id', usage.metadata.agent_history_id)
                .single()
              
              agentHistoryData = historyData
            } else if (usage.action_type.includes('_agent')) {
              // Cari berdasarkan waktu dan tipe agent (toleransi 5 menit)
              const timeStart = new Date(usage.created_at)
              timeStart.setMinutes(timeStart.getMinutes() - 5)
              const timeEnd = new Date(usage.created_at)
              timeEnd.setMinutes(timeEnd.getMinutes() + 5)
              
              // Use the action_type directly as it now matches agent_type format
              let agentType = usage.action_type
              
              const { data: historyData, error: historyError } = await supabase
                .from('agent_history')
                .select('title, query, agent_type')
                .eq('user_id', userId)
                .eq('agent_type', agentType)
                .gte('created_at', timeStart.toISOString())
                .lte('created_at', timeEnd.toISOString())
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle()
              
              if (historyError) {
                console.error('Usage history query error:', {
                  error: historyError,
                  errorCode: historyError.code,
                  errorMessage: historyError.message,
                  userId,
                  agentType,
                  timeRange: { start: timeStart.toISOString(), end: timeEnd.toISOString() }
                })
                
                // Handle 406 errors gracefully
                if (historyError.message?.includes('406') || historyError.code === '406') {
                  console.warn('Skipping agent history lookup due to authentication error')
                  agentHistoryData = null
                } else {
                  throw historyError
                }
              } else {
                agentHistoryData = historyData
              }
            }
            
            return {
              ...usage,
              title: agentHistoryData?.title || this.getDefaultTitle(usage.action_type),
              query: agentHistoryData?.query || usage.metadata?.query || '-',
              agent_type: agentHistoryData?.agent_type || usage.action_type
            }
          } catch {
            return {
              ...usage,
              title: this.getDefaultTitle(usage.action_type),
              query: usage.metadata?.query || '-',
              agent_type: usage.action_type
            }
          }
        })
      )
      
      const totalPages = Math.ceil(count / limit)
      
      return {
        success: true,
        data: enrichedData,
        pagination: {
          currentPage: page,
          totalPages,
          totalItems: count,
          itemsPerPage: limit,
          hasNextPage: page < totalPages,
          hasPrevPage: page > 1
        }
      }
    } catch (error) {
      console.error('Error fetching usage history:', error)
      return {
        success: false,
        error: error.message,
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: limit,
          hasNextPage: false,
          hasPrevPage: false
        }
      }
    }
  }
  
  /**
   * Mendapatkan statistik penggunaan user
   * @param {string} userId - ID user
   * @returns {Promise<Object>} Statistik penggunaan
   */
  static async getUsageStats(userId) {
    try {
      // Total credits used
      const { data: totalCredits } = await supabase
        .from('credit_usage_log')
        .select('credits_used')
        .eq('user_id', userId)
      
      const totalCreditsUsed = totalCredits?.reduce((sum, item) => sum + item.credits_used, 0) || 0
      
      // Usage by agent type
      const { data: agentUsage } = await supabase
        .from('credit_usage_log')
        .select('action_type, credits_used')
        .eq('user_id', userId)
      
      const usageByAgent = {}
      agentUsage?.forEach(item => {
        if (!usageByAgent[item.action_type]) {
          usageByAgent[item.action_type] = { count: 0, credits: 0 }
        }
        usageByAgent[item.action_type].count += 1
        usageByAgent[item.action_type].credits += item.credits_used
      })
      
      // Recent activity (last 30 days)
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      
      const { data: recentActivity } = await supabase
        .from('credit_usage_log')
        .select('credits_used')
        .eq('user_id', userId)
        .gte('created_at', thirtyDaysAgo.toISOString())
      
      const recentCreditsUsed = recentActivity?.reduce((sum, item) => sum + item.credits_used, 0) || 0
      
      return {
        success: true,
        data: {
          totalCreditsUsed,
          recentCreditsUsed,
          usageByAgent,
          totalActivities: totalCredits?.length || 0
        }
      }
    } catch (error) {
      console.error('Error fetching usage stats:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }
  
  /**
   * Cleanup data usage yang lebih dari 60 hari
   * @returns {Promise<Object>} Hasil cleanup
   */
  static async cleanupOldUsage() {
    try {
      const sixtyDaysAgo = new Date()
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60)
      
      // Cleanup credit_usage_log
      const { error: creditError } = await supabase
        .from('credit_usage_log')
        .delete()
        .lt('created_at', sixtyDaysAgo.toISOString())
      
      if (creditError) throw creditError
      
      // Cleanup agent_history (sudah ada function di database, tapi kita update ke 60 hari)
      const { error: historyError } = await supabase
        .from('agent_history')
        .delete()
        .lt('created_at', sixtyDaysAgo.toISOString())
      
      if (historyError) throw historyError
      
      return {
        success: true,
        message: 'Old usage data cleaned up successfully'
      }
    } catch (error) {
      console.error('Error cleaning up old usage:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }
  
  /**
   * Helper function untuk mendapatkan title default berdasarkan action_type
   * @param {string} actionType - Tipe action
   * @returns {string} Default title
   */
  static getDefaultTitle(actionType) {
    const titles = {
      'skripsi_agent': 'Skripsi Generation',
      'artikel_agent': 'Artikel Generation',
      'makalah_agent': 'Makalah Generation',
      'gambar_agent': 'Image Generation',
      'berita_agent': 'Berita Generation',
      'buku_agent': 'Buku Generation',
      'search_agent': 'Search Query',
      'trending_agent': 'Trending Analysis',
      'pdf_agent': 'PDF Analysis',
      'chat': 'Chat Conversation',
      'web_search': 'Web Search',
      'academic_search': 'Academic Search'
    }
    
    return titles[actionType] || actionType.replace('_', ' ').toUpperCase()
  }
  
  /**
   * Helper function untuk format action_type menjadi display name
   * @param {string} actionType - Tipe action
   * @returns {string} Display name
   */
  static formatActionType(actionType) {
    const formats = {
      'skripsi_agent': 'Skripsi Agent',
      'artikel_agent': 'Artikel Agent',
      'makalah_agent': 'Makalah Agent',
      'gambar_agent': 'Gambar Agent',
      'berita_agent': 'Berita Agent',
      'buku_agent': 'Buku Agent',
      'search_agent': 'Search Agent',
      'trending_agent': 'Trending Agent',
      'pdf_agent': 'PDF Agent',
      'chat': 'Chat',
      'web_search': 'Web Search',
      'academic_search': 'Academic Search'
    }
    
    return formats[actionType] || actionType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
  }
}

export default UsageService